import React, { useEffect, useRef, useState } from 'react';

interface CustomCursorProps {
  color?: string;
  size?: number;
  ringSize?: number;
  ringColor?: string;
  delay?: number;
  showOnMobile?: boolean;
}

export const CustomCursor: React.FC<CustomCursorProps> = ({
  color = '#000000',
  size = 8,
  ringSize = 40,
  ringColor = 'rgba(0, 0, 0, 0.15)',
  delay = 0.08,
  showOnMobile = false,
}) => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorRingRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isPointer, setIsPointer] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0 });

  // Check if device supports hover (non-touch device)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover)');
    setIsVisible(showOnMobile || mediaQuery.matches);
  }, [showOnMobile]);

  useEffect(() => {
    if (!isVisible) return;

    const moveCursor = (e: MouseEvent) => {
      setTargetPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseDown = () => setIsActive(true);
    const handleMouseUp = () => setIsActive(false);

    const handleMouseEnter = () => {
      setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    // Check for elements that should show pointer cursor
    const checkPointerElements = () => {
      const element = document.elementFromPoint(targetPosition.x, targetPosition.y);
      if (!element) return;

      const computedStyle = window.getComputedStyle(element);
      const cursor = computedStyle.getPropertyValue('cursor');
      
      // Check if element or its parent has a click handler
      const hasPointerCursor = element.hasAttribute('onClick') || 
                              element.tagName === 'BUTTON' || 
                              element.tagName === 'A' ||
                              element.closest('button') !== null || 
                              element.closest('a') !== null ||
                              cursor === 'pointer';
      
      setIsPointer(hasPointerCursor);
    };

    // Animation frame for smooth cursor movement
    let animationFrameId: number;
    let currentPosition = { ...position };

    const animateCursor = () => {
      // Calculate distance between current and target position
      const dx = targetPosition.x - currentPosition.x;
      const dy = targetPosition.y - currentPosition.y;

      // Move current position a percentage of the way to target
      currentPosition.x += dx * (1 - delay);
      currentPosition.y += dy * (1 - delay);

      setPosition(currentPosition);
      checkPointerElements();

      animationFrameId = requestAnimationFrame(animateCursor);
    };

    animationFrameId = requestAnimationFrame(animateCursor);

    document.addEventListener('mousemove', moveCursor);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', moveCursor);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, [delay, isVisible, position.x, position.y, targetPosition]);

  if (!isVisible) return null;

  return (
    <>
      <div
        ref={cursorRef}
        className="custom-cursor"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: color,
          transform: `translate(${position.x}px, ${position.y}px)`,
          opacity: isVisible ? 1 : 0,
          mixBlendMode: 'difference',
          zIndex: 9999,
        }}
      />
      <div
        ref={cursorRingRef}
        className={`custom-cursor-ring ${isActive ? 'active' : ''} ${isPointer ? 'pointer' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          width: `${isPointer ? ringSize * 0.5 : ringSize}px`,
          height: `${isPointer ? ringSize * 0.5 : ringSize}px`,
          borderColor: ringColor,
          transform: `translate(${position.x}px, ${position.y}px) scale(${isActive ? 0.8 : 1})`,
          opacity: isVisible ? 0.5 : 0,
          zIndex: 9999,
        }}
      />
    </>
  );
};

export default CustomCursor;
